import { useState } from "react";
import { useAuth } from "./auth/AuthContext";
import { AuthScreen } from "./components/AuthScreen";
import { DesktopLayout } from "./components/DesktopLayout";
import { HomeTab } from "./components/HomeTab";
import { ConditionsTab } from "./components/ConditionsTab";
import { TrafficTab } from "./components/TrafficTab";
import { LodgingTab } from "./components/LodgingTab";
import { CarpoolTab } from "./components/CarpoolTab";
import { ProfileOverlay } from "./components/ProfileOverlay";
import { GuestPreview, GuestLocked } from "./components/GuestPreview";
import { InstallPrompt } from "./components/InstallPrompt";

const tabs = [
  { id: "home", label: "Home" },
  { id: "conditions", label: "Conditions" },
  { id: "traffic", label: "Traffic" },
  { id: "lodging", label: "Lodging" },
  { id: "carpool", label: "Carpool" },
];

const DEFAULT_AUTH_PROMPT =
  "Sign in or create an account to save your home base, pass, and get carpool matches for Tahoe.";

// One line: the specific reason a guest hit this wall, plus the standing value prop — same pattern as
// Diver's Horizon's authPromptFor(reason), not a generic "please sign in."
function authPromptFor(reason) {
  return `Sign in or create an account to ${reason} — plus save your home base and pass, and post/find carpool rides.`;
}

function App() {
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState("home");
  const [showProfile, setShowProfile] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);

  const openAuthScreen = (reason) => setAuthPrompt(reason ? authPromptFor(reason) : DEFAULT_AUTH_PROMPT);
  const closeAuthScreen = () => setAuthPrompt(null);

  if (loading) return <div className="app-loading">Loading…</div>;

  // A guest can browse Home/Conditions/Traffic/Lodging — only Conditions/Traffic get a blurred preview
  // (the app's actual pitch, per CLAUDE.md); Carpool is a hard sign-in wall since its data is
  // Cognito-gated on the backend too (nothing to preview). Nav stays identical either way — label, don't
  // hide, same philosophy as the demo-data badge and pass-based resort sorting.
  function renderActiveTab() {
    switch (activeTab) {
      case "conditions":
        return user ? (
          <ConditionsTab />
        ) : (
          <GuestPreview
            message={authPromptFor("see live conditions and report base depth")}
            onSignIn={() => openAuthScreen("see live conditions and report base depth")}
          >
            <ConditionsTab />
          </GuestPreview>
        );
      case "traffic":
        return user ? (
          <TrafficTab />
        ) : (
          <GuestPreview
            message={authPromptFor("see I-80/US-50 chain control")}
            onSignIn={() => openAuthScreen("see I-80/US-50 chain control")}
          >
            <TrafficTab />
          </GuestPreview>
        );
      case "lodging":
        return <LodgingTab />;
      case "carpool":
        return user ? (
          <CarpoolTab />
        ) : (
          <GuestLocked
            message={authPromptFor("see and post carpool rides")}
            onSignIn={() => openAuthScreen("see and post carpool rides")}
          />
        );
      case "home":
      default:
        return <HomeTab onSignIn={user ? undefined : () => openAuthScreen()} />;
    }
  }

  const identity = user?.signInDetails?.loginId || user?.username || "";
  const initial = identity.charAt(0).toUpperCase() || "?";

  return (
    <DesktopLayout tabs={tabs} activeTab={activeTab} onSelectTab={setActiveTab}>
      <div className="wrap">
        <div className="hd-row">
          <div />
          {user ? (
            <button className="av" onClick={() => setShowProfile(true)} aria-label="Profile">
              {initial}
            </button>
          ) : (
            <button className="pill" onClick={() => openAuthScreen()}>
              Sign in
            </button>
          )}
        </div>
        {renderActiveTab()}
      </div>
      {showProfile && user && <ProfileOverlay onClose={() => setShowProfile(false)} />}
      {authPrompt && (
        <div className="sheet">
          <AuthScreen promptMessage={authPrompt} onCancel={closeAuthScreen} onSignedIn={closeAuthScreen} />
        </div>
      )}
      {!authPrompt && !showProfile && <InstallPrompt />}
    </DesktopLayout>
  );
}

export default App;
